import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ui } from '../../../config/uiSizes';

interface ParameterRow {
  id: string | number;
  [key: string]: unknown;
}

interface ParametersLayoutProps<T extends ParameterRow> {
  title: string;
  description?: string;
  addButtonLabel: string;
  rows: T[];
  onAddRow: () => void;
  onRemoveRow: (id: string | number) => void;
  renderRow: (row: T, index: number, onRemove: () => void) => React.ReactNode;
}

export const ParametersLayout = <T extends ParameterRow>({
  title,
  description,
  addButtonLabel,
  rows,
  onAddRow,
  onRemoveRow,
  renderRow,
}: ParametersLayoutProps<T>) => {
  return (
    <div className={`border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 ${ui.page.cardPadding}`}>
      {/* Header */}
      <div className={`${ui.mb.title}`}>
        <h3 className={`${ui.text.sectionTitle} font-bold text-gray-900 dark:text-gray-100`}>{title}</h3>
        {description && (
          <p className={`${ui.text.label} text-gray-600 dark:text-gray-400 mt-1`}>{description}</p>
        )}
      </div>

      {/* Rows */}
      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {rows.map((row, index) => (
            <motion.div
              key={row.id}
              layout
              initial={{ opacity: 0, height: 0, scale: 0.95 }}
              animate={{ opacity: 1, height: 'auto', scale: 1 }}
              exit={{ opacity: 0, height: 0, scale: 0.95 }}
              transition={{
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1],
              }}
            >
              {renderRow(row, index, () => onRemoveRow(row.id))}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Add Button */}
      <button
        type="button"
        onClick={onAddRow}
        className={`mt-4 flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors ${ui.text.label} font-medium`}
      >
        <svg
          className={`${ui.svg.sm}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
        {addButtonLabel}
      </button>
    </div>
  );
};
